/* @flow */
import * as React from "react";
import * as ReactDOM from "react-dom";
import { connect } from "react-redux";

import Socket, {
  Subscription,
  StockSubscription,
  PercentSubscription
} from "../components/Socket";
import dataLayer from "./common/dataLayer";

import { ModalConsumer } from "./common/ModalProvider";
import TournamentApi from "./Tournaments/Api";
import * as Tournament from "./Tournaments/Popups";

import type { State } from "../redux/reducer";
import type { Dispatch } from "../redux/action";
import type { SocketMessage } from "../redux/CommonTypes";
import { localStorage } from "../components/webview/Rate";

type Props = {
  showModal: Function,
  user_key: ?string,
  domain: ?string,
  port: ?string,
  stocks: Array<string>,
  socket: $PropertyType<State, "socket">,
  onRefreshFrontend: *,
  onSocketMessage: *,
  onSocketStateChange: *,
  onTick: () => void,
  api: {
    fetchEntity: Function,
    clearEntities: Function
  },
  onUpdateProfile: (payload: Object) => void
};

function makeSocketUrl({ user_key, domain, port }) {
  if (user_key == null || domain == null || port == null) {
    throw new TypeError("Not enough information to open a connection");
  }

  if (String(port) === "6443" || String(port) === "443") {
    return `wss://${domain}:${port}/ws/${user_key}`;
  }

  return `ws://${domain}:${port}/ws/${user_key}`;
}

function openDealsFilter(deal) {
  // Open deal has time and no profit
  return deal.time !== undefined && deal.profit === undefined;
}

class ReconnectableSocket extends React.Component<
  Props,
  { hackerUrl: ?string }
> {
  interval: number;

  state = {
    hackerUrl: null
  };

  tick = () => {
    this.props.onTick();
  };

  updateProfile = async () => {
    const payload = await this.props.api.fetchEntity("/api/profile");

    if (payload != null) {
      this.props.onUpdateProfile(payload);
    }
  };

  onNotificationsMessage = (message: SocketMessage) => {
    this.props.api.fetchEntity("/api/bell-tolls-items");

    this.props.onSocketMessage(message);
  };

  componentDidMount() {
    this.interval = setInterval(this.tick, 1000);

    window.hijackSocket = url => {
      this.setState({ hackerUrl: url + this.props.user_key });
    };
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  render() {
    if (
      this.props.pass ||
      this.props.user_key == null ||
      this.props.domain == null ||
      this.props.port == null ||
      this.props.socket.connectionState === WebSocket.CLOSED
    ) {
      return null;
    }

    const tabs = [...this.props.tabs];
    const openStocks = this.props.deals
      .filter(openDealsFilter)
      .reduce((res, deal) => {
        if (deal) {
          res.push(deal.stock);
        }
        return res;
      }, tabs);
    const stocks = [...new Set(openStocks)];

    return (
      <Socket
        url={this.state.hackerUrl || makeSocketUrl(this.props)}
        readyState={this.props.socket.connectionState}
        onChange={e => {
          if (e.target.readyState === WebSocket.CLOSED) {
            dataLayer({
              event: "event-to-ga",
              eventCategory: "Socket Error",
              eventAction: "Socket's been closed",
              eventLabel: `Failed to connect ${this.props.socket
                .failedReconnects + 1}x times`
            });
          }

          this.props.onSocketStateChange(e);
        }}
      >
        <Subscription op="statPush_go" onMessage={this.props.onSocketMessage} />
        <Subscription
          op="phone-confirm-call"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="statusChanged"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="operationOpened"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="tournamentOperationOpened"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="tournamentOperationClosed"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="tournament-status-change"
          onMessage={message => {
            let component = null;
            let modalProps = {};

            if (message.status === "started") {
              component = Tournament.StartPopup;
              modalProps = {
                id: message.id
              };
            }

            if (message.status === "ended") {
              component = Tournament.FinalPopup;
              modalProps = {
                ...message.stats,
                id: message.id
              };
            }
            if (component !== null) {
              this.props.showModal(component, modalProps);
            }

            this.props.api.fetchEntity("/api/bell-tolls-items");
            this.props.onSocketMessage(message);
          }}
        />
        <Subscription
          op="announcements"
          onMessage={message => {
            switch (message.type) {
              case "tournament":
                this.props.api.fetchEntity("/api/bell-tolls-items");
                this.props.api.fetchEntity("/api/tournaments/list?status=1");
                break;
              case "invited-tournament":
                this.props.api.fetchEntity("/api/bell-tolls-items");
                this.props.api
                  .fetchEntity(
                    `/api/tournaments/info?id=${
                      message["2x_demo_tournament_id"]
                    }`
                  )
                  .then(data => {
                    if (
                      data.value["2x_demo_balance_invite"] &&
                      data.value.participation_status === 0
                    ) {
                      return this.props.onSocketMessage({
                        op: "demo-tournament",
                        ...data.value
                      });
                    }
                  });
                break;
              case "news":
                this.props.api.clearEntities("/helpdesk/api/news");
                this.props.api.fetchEntity("/helpdesk/api/news");
                this.props.api.fetchEntity("/api/news");
                break;
              default:
                this.props.api.fetchEntity("/api/bell-tolls-items");
                break;
            }

            this.props.onSocketMessage(message);
          }}
        />
        <Subscription
          op="withdraw"
          onMessage={message => {
            if (message.info.status === "success") {
              this.updateProfile();
            }

            this.onNotificationsMessage(message);
          }}
        />
        <Subscription
          op="verification"
          onMessage={message => {
            this.updateProfile();
            this.onNotificationsMessage(message);
          }}
        />
        <Subscription
          op="unconfirmed_push_subscription"
          onMessage={this.onNotificationsMessage}
        />
        <Subscription
          op="unconfirmed_email_subscription"
          onMessage={this.onNotificationsMessage}
        />
        <Subscription
          op="deposit-bonus-inserted"
          onMessage={message => {
            // Мы получаем события для обеих сторорн реферрального бонуса.
            // Здесь мы решаем, какая из сторон — мы.
            if (this.props.user_id === message.sql_user_id) {
              this.props.onSocketMessage(message);
            }
          }}
        />
        <Subscription
          op="operationClosed"
          onMessage={message => {
            const ua = window.navigator.userAgent.toLowerCase();
            // Android Webview full app
            const isWebView = /wvbo_full/g.test(ua);
            // Android Webview analytics
            const isAnalyticsWebView =
              process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && /androidweb.*com\.irontrade\.analytics.*wvbo/g.test(ua) &&
              !isWebView;
            if (isWebView || isAnalyticsWebView) {
              const [error, store] = localStorage();
              const { KEY_DEALS_COUNT } = localStorage.keys;
              if (error == null && Number(message.profit) > 0) {
                const current = store.get(KEY_DEALS_COUNT) || 0;
                if (current !== "WASTED") {
                  store.set(KEY_DEALS_COUNT, Number(current) + 1);
                }
              }
            }
            this.props.onSocketMessage(message);
          }}
        />
        <Subscription
          op="compensation-updated"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="compensation-created"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="refreshFrontend"
          onMessage={this.props.onRefreshFrontend}
        />
        <Subscription
          op="turnoverChanged"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="deposit"
          onMessage={message => {
            if (message.info.status === "success") {
              this.updateProfile();
            }
            this.onNotificationsMessage(message);
          }}
        />
        <Subscription
          op="user-accounts-updated"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="schedule-updated"
          onMessage={this.props.onSocketMessage}
        />
        <Subscription
          op="social_trading"
          onMessage={this.props.onSocketMessage}
        />
        {stocks.map(stock => [
          <StockSubscription
            key={stock + "_points"}
            stock={stock}
            onMessage={this.props.onSocketMessage}
          />,
          <PercentSubscription
            key={stock + "_percent"}
            stock={stock}
            onMessage={this.props.onSocketMessage}
          />
        ])}
      </Socket>
    );
  }
}

function mapStateToProps(state: State) {
  return {
    user_id: state.profile && state.profile.id,
    user_key: state.profile && state.profile.user_key,
    domain: state.serverData && state.serverData.websocketDomain,
    port: state.serverData && state.serverData.websocketWebPort,
    tabs: state.tabs.tabs,
    deals: state.deals,
    socket: state.socket
  };
}

let timer = null;
let batch = [];

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    onRefreshFrontend() {
      window.location.reload(/* force: */ true);
    },
    onSocketMessage(message: SocketMessage) {
      batch.push({
        type: "RECEIVE_SOCKET_MESSAGE",
        payload: message
      });

      if (timer == null) {
        timer = setTimeout(() => {
          ReactDOM.unstable_batchedUpdates(() => {
            batch.forEach(dispatch);
            batch = [];
          });

          timer = null;
        }, 600);
      }
    },
    onSocketStateChange(event) {
      if (typeof event.target.readyState === "number") {
        dispatch({
          type: "SOCKET_STATE_CHANGE",
          payload: event.target.readyState
        });
      }
    },
    onTick() {
      dispatch({ type: "SOCKET_RECONNECT_TICK" });
    },
    onUpdateProfile(profile) {
      dispatch({
        type: "RECEIVE_PROFILE",
        payload: profile.value
      });
    }
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)((props: Props) => (
  <TournamentApi.Consumer>
    {api => (
      <ModalConsumer>
        {({ showModal }) => (
          <ReconnectableSocket {...props} api={api} showModal={showModal} />
        )}
      </ModalConsumer>
    )}
  </TournamentApi.Consumer>
));



// WEBPACK FOOTER //
// ./src/components/ReconnectableSocket.js